import React, { memo } from 'react';
import classNames from 'classnames';
import IconFont from '$/components/IconFont';
import { handleImageUrl } from '$/utils/image';
import { getVideoCoverUrl } from '$/utils/upload';
import { Config } from '@/common/config';
import { useLanguageStore } from '$/store/languageStore';
import { isVideoUrl } from '@/utils';
import styles from './index.module.scss';

export interface AIToolItem {
  id: string;
  title: string;
  description: string;
  icon: string;
  route: Config['Pathname'];
  url?: string;
  badgeText?: string;
  tipIcons?: string[];
}

type ToolCardProps = {
  item: AIToolItem;
  fixedWidth?: boolean;
  onClick?: (item: AIToolItem) => void;
  useTranslation?: boolean;
  hasMaskButton?: boolean;
  className?: string;
  /** 首屏 LCP 候选卡片：封面图用高优先级加载，减少 LCP 发现延迟。 */
  priority?: boolean;
};

const TOOL_CARD_COVER_SIZE = 122;

/**
 * 工具卡片封面：视频取首帧封面后再缩略，图片直接缩略。
 */
function getToolCardCoverSrc(url: string): string {
  const sourceUrl = url.trim();
  if (!sourceUrl) return '';
  const coverSource = isVideoUrl(sourceUrl) ? getVideoCoverUrl(sourceUrl) : sourceUrl;
  return handleImageUrl(coverSource, { mode: 3, width: TOOL_CARD_COVER_SIZE, height: TOOL_CARD_COVER_SIZE });
}

/**
 * 渲染单个工具卡片。
 *
 * 封面图以真实 `<img>` 渲染（而非 CSS background-image），以便浏览器更早发现、
 * 并可对首屏 LCP 卡片设置 `fetchpriority="high"`，降低 LCP 加载延迟。
 */
function ToolCard({
  item, fixedWidth, onClick, useTranslation, hasMaskButton,
  className, priority,
}: ToolCardProps) {
  const languageStore = useLanguageStore((state) => state.languageStore);

  /**
   * 阻止点击卡片时抢走焦点，避免首页输入框失焦触发 BorderBeam 尺寸切换出现白底闪烁。
   */
  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (onClick) {
      event.preventDefault();
    }
  };

  return (
    <div
      className={classNames(styles.toolCard, {
        [styles.fixedToolCard]: fixedWidth,
        [styles.coverToolCard]: hasMaskButton,
      }, className)}
      onMouseDown={handleMouseDown}
      onClick={() => onClick?.(item)}
    >
      {item.url ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          className={styles.coverImage}
          src={getToolCardCoverSrc(item.url)}
          alt=""
          decoding="async"
          loading={priority ? 'eager' : 'lazy'}
          fetchPriority={priority ? 'high' : 'auto'}
        />
      ) : null}

      {item.icon ? (
        <span className={styles.iconBox}>
          <IconFont name={item.icon} fontSize={18} />
          {item.badgeText ? <span className={styles.hotBadge}>{item.badgeText}</span> : null}
        </span>
      ) : null}

      {item.tipIcons?.length ? (
        <span className={styles.tipIconList}>
          {item.tipIcons.map((icon) => (
            <span className={styles.tipIconBox} key={icon}>
              <IconFont name={icon} fontSize={12} />
            </span>
          ))}
        </span>
      ) : null}

      <span className={styles.toolCopy}>
        <span className={styles.toolTitle}>{useTranslation ? languageStore.smartcatTranslationText((v) => v[item.title as keyof typeof v]) : item.title}</span>
        {item.description ? <span className={styles.toolDesc}>{useTranslation ? languageStore.smartcatTranslationText((v) => v[item.description as keyof typeof v]) : item.description}</span> : null}
        {hasMaskButton && (
        <span className={styles.coverHoverButton}>
          {languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_studio_chatcard_ppt_edit)}
          <IconFont name="arrowRightBold" fontSize={12} />
        </span>
        )}
      </span>

    </div>
  );
}

export default memo(ToolCard);
